import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { InfoBanner, ProgressBar, TableHeader } from "@services/ui-components";
import { Col, Row } from "react-bootstrap";
import { Link, Navigate, useParams } from "react-router-dom";
import { getSteps, IStep } from "../Me-menu/MenuItemsPage/constants";
import { SelectedCategoryStateInterface } from "./interfaces/interface";
import EditMenuCategoryComponent from "./CategoryComponent/CategoryComponent";
import MenuByCategoryComponent from "./MenuByCategoryComponent/MenuByCategoryComponent";
import { useToast } from "../../hooks/useToast";
import ToastComponent from "../ui-components/ToastComponent/ToastComponent";
import MenuLanguageVersionSelectContainer from "../Me-menu/components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { getMenuStep } from "./helpers/helpers";
import { useGetIsAnyItemsPerMenuQuery } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import { useGetOneMenuQuery } from "@me-team/host/main/menu/graphql/menu.hooks";
import { cloneNameToDisplay } from "@me-team/common/src/components/Me-menu/utils/cloneNameUtils";
import { getStatusBannerInfo } from "../Me-menu/AllMenusPage/helpers";
import { canEditMenuContentByStatus, toMenuStatusEnum } from "../Me-menu/AllMenusPage/menuEnumMappings";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { CompanyAccess } from "@me-team/host/main/graphql/types";

const MenuEditContent: React.FC = () => {
    const { t: translate } = useTranslation();
    const { id, branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateMenu = useRoleAccess(CompanyAccess.MenuCreate);
    const canCloneEditMenu = useRoleAccess(CompanyAccess.MenuCloneEdit);
    const steps: IStep[] = getSteps(translate);
    const { showToast, toastText, setShowToast } = useToast();

    const [selectedCategory, setSelectedCategory] = useState<SelectedCategoryStateInterface>({
        categoryId: null,
        childCategoryId: null,
        categoryName: null,
        categoryNameOrigin: null,
    });

    const { data, loading, refetch } = useGetIsAnyItemsPerMenuQuery({
        skip: !currentCompanyId,
        fetchPolicy: "network-only",
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            menuId: Number(id),
        },
    });

    const isAnyItems = data?.user.company.branches[0].menusList[0].isAnyItems ?? false;

    const { data: dataOneMenu, loading: dataOneMenuLoading } = useGetOneMenuQuery({
        skip: !currentCompanyId,
        fetchPolicy: "network-only",
        variables: {
            companyId: Number(currentCompanyId),
            branchId: Number(branchId),
            menuId: Number(id),
        },
    });

    const menuOneData = dataOneMenu?.user?.company?.branches?.[0]?.menusList?.[0];
    const menuName = menuOneData?.name ?? "";
    const menuStatusSlug = menuOneData?.status?.slug;
    const menuDisplayName = cloneNameToDisplay(menuName, translate);
    const menuStatusEnum = toMenuStatusEnum(menuStatusSlug) ?? undefined;
    const menuStatusBanner = getStatusBannerInfo(menuStatusEnum, translate);
    const canEditMenuContent = canEditMenuContentByStatus(menuStatusSlug, canCreateMenu, canCloneEditMenu);

    const isSaveButtonDisabled = !isAnyItems || loading;

    const refreshMenuState = useCallback(async () => {
        await refetch();
    }, [refetch]);

    const [currentStep, setCurrentStep] = useState<number | null>(null);

    const onCategorySelect = useCallback((selectedCategory: SelectedCategoryStateInterface) => {
        setSelectedCategory(selectedCategory);
        const step = getMenuStep(selectedCategory, []);
        setCurrentStep(step);
    }, []);

    if (!currentCompanyId || loading || dataOneMenuLoading) return null;

    if (!canEditMenuContent) {
        return <Navigate to="/error/403" replace />;
    }

    return (
        <div className="mb-4">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {translate(
                        "On this page, you can change the menu content and the order in which categories and dishes are displayed."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={`${translate("menu")} ${menuDisplayName}`}
                paths={[
                    { path: `/branches/${branchId}`, label: translate("Branches") },
                    { path: `/menu/all-menus/branch/${branchId}`, label: translate("All menu") },
                    {
                        path: `/menu/page/${id}/branch/${branchId}`,
                        label: `${translate("menu")} ${menuDisplayName}`,
                    },
                ]}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <Col md={6} lg={7} xl={6} xxl={4} className="mb-2 mb-xxl-0" style={{ minWidth: "220px" }}>
                            <div className="position-relative">
                                <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                    {translate("Language version")}
                                </span>
                                <MenuLanguageVersionSelectContainer
                                    companyId={Number(currentCompanyId)}
                                    menuFillingContext={{
                                        branchId: Number(branchId),
                                        menuId: Number(id),
                                    }}
                                    showAddLanguageOption={false}
                                />
                            </div>
                        </Col>
                        <Col md={6} lg={7} xl={6} xxl={4}>
                            <Link
                                to={`/menu/${id}/preview/branch/${branchId}`}
                                className={`btn btn-primary fw-bold w-100 text-truncate ${isSaveButtonDisabled ? "disabled" : ""}`}
                                onClick={(e) => {
                                    if (isSaveButtonDisabled) {
                                        e.preventDefault();
                                    }
                                }}
                            >
                                {translate("View")}
                            </Link>
                        </Col>
                    </Row>
                }
                colSize={6}
                btnColSize={6}
            ></TableHeader>
            <ProgressBar currentStep={currentStep} steps={steps} marginBottom="mb-0" isMenu />

            {/* Show this info block if failed to move the dish*/}
            {/*<InfoBanner backgroundColor="danger-light" textColor="danger" classIcon="bi-exclamation-circle" marginClasses="mt-4 mb-0" isCloseButton>*/}
            {/*    {t("Failed to move the dish to another category")}*/}
            {/*</InfoBanner>*/}

            {menuStatusBanner ? (
                <InfoBanner
                    backgroundColor={menuStatusBanner.backgroundColor}
                    textColor={menuStatusBanner.textColor}
                    classIcon={menuStatusBanner.icon}
                    marginClasses="mt-4 mb-0"
                >
                    {menuStatusBanner.text}
                </InfoBanner>
            ) : null}

            <Row className="gx-5 mt-4 pt-6">
                <Col xl={5} className="mb-4 mb-xl-0 d-flex flex-column">
                    <EditMenuCategoryComponent
                        onCategorySelect={onCategorySelect}
                        selectedCategory={selectedCategory}
                        onMenuStructureChanged={refreshMenuState}
                        canEditMenuContent={canEditMenuContent}
                    />
                </Col>

                <MenuByCategoryComponent
                    selectedCategory={selectedCategory}
                    onStepChange={setCurrentStep}
                    onMenuStructureChanged={refreshMenuState}
                    canEditMenuContent={canEditMenuContent}
                />
            </Row>
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};

export default MenuEditContent;
